import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  CreditCard,
  Landmark,
  Banknote,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Loader2,
  Upload,
  FileText,
  Download,
  Clock,
  Info,
  AlertTriangle
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { AssociateData } from '../services/otpAuthService';
import {
  getPaymentSettings,
  createPaymentIntent,
  confirmPayment,
  requestBankTransfer,
  requestRid,
  downloadMandatoSepa,
  uploadMandatoSepa,
  salvaCartaPendente,
  rimuoviCartaPendente,
  isConfermaCartaDefinitiva,
  annoDellaQuota,
  VoceQuota,
  RataRID,
  DettagliBancari
} from '../services/associatePaymentService';

// Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

type PaymentMethod = 'carta' | 'bonifico' | 'rid';
type Step = 'summary' | 'method' | 'payment' | 'success' | 'charged_pending';

// Esito del pagamento con carta passato allo step finale
interface CardSuccessInfo {
  duplicate?: boolean;
  message?: string;
}

// La conferma backend e' idempotente: ritentabile su errori di rete / 5xx / 429
const CONFIRM_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

const confirmPaymentWithRetry = async (paymentIntentId: string) => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await confirmPayment(paymentIntentId);
    } catch (err: any) {
      const status = err?.response?.status;
      const retriable = !err?.response || status >= 500 || status === 429;
      if (!retriable || attempt >= CONFIRM_RETRY_DELAYS_MS.length) throw err;
      await new Promise((resolve) => setTimeout(resolve, CONFIRM_RETRY_DELAYS_MS[attempt]));
    }
  }
};

interface AssociateFeePaymentProps {
  associate: AssociateData;
  onPaymentComplete: () => void;
  onClose: () => void;
  disableRid?: boolean;
  /** Se passato, mostra solo queste voci (pagamento integrativo) invece di caricare le settings complete */
  overrideVociQuota?: VoceQuota[];
  overrideTotale?: number;
  /** Pagamento integrativo pre-creato da confermare (invece della quota nazionale) */
  paymentId?: string | null;
}

// ========================================
// Stripe Card Payment Form
// ========================================
const CardPaymentForm: React.FC<{
  vociQuota: VoceQuota[];
  totale: number;
  paymentId?: string | null;
  /** Associato e anno della quota: chiave e contenuto del promemoria «carta addebitata» */
  associateId?: string;
  anno: number;
  onSuccess: (info?: CardSuccessInfo) => void;
  onCancel: () => void;
  /** Il server ha calcolato un importo diverso da quello mostrato */
  onAmountChanged: (amount: number) => void;
  /** Il pagamento non e' piu' valido lato server (409/404): chiudere e ricaricare */
  onStale: () => void;
  /** Carta addebitata ma registrazione backend non riuscita */
  onChargedPending: () => void;
  onProcessingChange?: (processing: boolean) => void;
}> = ({ vociQuota, totale, paymentId, associateId, anno, onSuccess, onCancel, onAmountChanged, onStale, onChargedPending, onProcessingChange }) => {
  const toast = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessingState] = useState(false);
  // PaymentIntent gia' creato il cui importo attende la riconferma dell'utente
  const pendingIntentRef = useRef<{ clientSecret: string; paymentIntentId: string; amount: number } | null>(null);

  const setProcessing = (value: boolean) => {
    setProcessingState(value);
    onProcessingChange?.(value);
  };

  const isDarkMode = document.documentElement.classList.contains('dark');
  const stripeElementStyle = {
    base: {
      fontSize: '16px',
      color: isDarkMode ? '#e5e7eb' : '#424770',
      '::placeholder': {
        color: isDarkMode ? '#9ca3af' : '#aab7c4',
      },
    },
    invalid: {
      color: '#ef4444',
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    try {
      // 1. Crea PaymentIntent (importi calcolati server-side).
      //    Se l'utente sta riconfermando un importo aggiornato, riusa l'intent gia' creato.
      let data = pendingIntentRef.current;
      if (data && Math.abs(data.amount - totale) > 0.01) data = null;
      pendingIntentRef.current = null;

      if (!data) {
        try {
          data = (await createPaymentIntent(paymentId)).data;
        } catch (err: any) {
          const status = err.response?.status;
          if (status === 409 || status === 404) {
            // Pagamento gia' esistente / sede gia' pagata / pending non trovato:
            // lo stato mostrato e' superato, chiudi e ricarica la card.
            toast.error(err.response?.data?.message || 'Pagamento non più disponibile', { duration: 8000 });
            onStale();
            return;
          }
          throw err;
        }

        // L'importo che verra' addebitato deve coincidere con quello mostrato
        if (typeof data.amount === 'number' && Math.abs(data.amount - totale) > 0.01) {
          pendingIntentRef.current = data;
          onAmountChanged(data.amount);
          toast.warning(
            `L'importo è stato aggiornato a € ${data.amount.toFixed(2)}: verifica e conferma di nuovo`,
            { duration: 10000 }
          );
          return;
        }
      }

      // 2. Conferma con Stripe
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) throw new Error('Card element non trovato');

      const { error, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: { card: cardNumberElement }
      });

      if (error) throw new Error(error.message);

      if (paymentIntent?.status === 'succeeded') {
        // 3. Conferma nel backend. Da qui la carta e' GIA' addebitata:
        //    in caso di errore non si riabilita mai il form.
        //    Il promemoria nel browser impedisce alla card di riproporre «Paga»
        //    finché la registrazione non è confermata (anche dopo un ricaricamento).
        salvaCartaPendente(associateId, {
          paymentIntentId: data.paymentIntentId,
          anno,
          ...(paymentId ? { paymentId } : {})
        });
        let confirmRes;
        try {
          confirmRes = await confirmPaymentWithRetry(data.paymentIntentId);
        } catch (confirmError: any) {
          console.error('Pagamento addebitato ma conferma backend fallita:', confirmError);
          if (isConfermaCartaDefinitiva(confirmError)) rimuoviCartaPendente(associateId, data.paymentIntentId);
          onChargedPending();
          return;
        }
        // Registrato (anche come «già registrato» o come possibile duplicato)
        rimuoviCartaPendente(associateId, data.paymentIntentId);

        // Doppio addebito: lo segnala `duplicate` oppure il pagamento restituito
        // (es. «già registrato» dal webhook come duplicato) con isDuplicate.
        const isDoppio = confirmRes?.data?.duplicate === true || confirmRes?.data?.payment?.isDuplicate === true;
        if (isDoppio) {
          const message = (confirmRes.data.duplicate === true && confirmRes.data.message)
            || 'Il pagamento è stato registrato come possibile doppio addebito: la segreteria verificherà.';
          toast.warning(message, { duration: 10000 });
          onSuccess({ duplicate: true, message });
        } else {
          toast.success('Pagamento confermato!', { duration: 5000 });
          onSuccess();
        }
      }
    } catch (error: any) {
      console.error('Errore pagamento carta:', error);
      toast.error(error.response?.data?.message || error.message || 'Errore durante il pagamento');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Dati Carta di Credito
        </label>

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Numero Carta
          </label>
          <div className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700">
            <CardNumberElement options={{ style: stripeElementStyle }} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Scadenza (MM/AA)
            </label>
            <div className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700">
              <CardExpiryElement options={{ style: stripeElementStyle }} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              CVC
            </label>
            <div className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700">
              <CardCvcElement options={{ style: stripeElementStyle }} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Elaborazione...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Paga {totale.toFixed(2)} EUR
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Indietro
        </button>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        Pagamento sicuro tramite Stripe. I dati della carta non vengono memorizzati.
      </p>
    </form>
  );
};

// ========================================
// Bank Transfer Form
// ========================================
const BankTransferForm: React.FC<{
  vociQuota: VoceQuota[];
  totale: number;
  dettagliBancari: DettagliBancari;
  associate: AssociateData;
  anno: number;
  paymentId?: string | null;
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ vociQuota, totale, dettagliBancari, associate, anno, paymentId, onSuccess, onCancel }) => {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Solo file PDF, JPG o PNG sono accettati');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('Il file non puo superare 10MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setProcessing(true);
    try {
      const formData = new FormData();
      if (file) {
        formData.append('distinta', file);
      }
      // Le voci quota sono calcolate server-side; per i pagamenti integrativi
      // si invia solo il paymentId del pagamento pre-creato.
      if (paymentId) {
        formData.append('paymentId', paymentId);
      }

      await requestBankTransfer(formData);

      toast.success('Bonifico registrato. In attesa di conferma dalla segreteria.', { duration: 5000 });
      onSuccess();
    } catch (error: any) {
      console.error('Errore richiesta bonifico:', error);
      toast.error(error.response?.data?.message || 'Errore durante la richiesta');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Coordinate bancarie */}
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <Landmark className="w-4 h-4" />
          Coordinate Bancarie
        </h4>
        <div className="space-y-2 text-sm">
          <p className="text-gray-700 dark:text-gray-300">
            <strong>IBAN:</strong> <span className="font-mono select-all">{dettagliBancari.iban}</span>
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            <strong>Banca:</strong> {dettagliBancari.banca}
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            <strong>Intestatario:</strong> {dettagliBancari.intestatario}
          </p>
          {dettagliBancari.bic && (
            <p className="text-gray-700 dark:text-gray-300">
              <strong>BIC/SWIFT:</strong> {dettagliBancari.bic}
            </p>
          )}
        </div>
      </div>

      {/* Importo e causale */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-900 dark:text-blue-100 mb-2">
          <strong>Importo da versare:</strong> {totale.toFixed(2)} EUR
        </p>
        <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded border border-amber-200 dark:border-amber-700">
          <p className="text-xs text-amber-800 dark:text-amber-200">
            <strong>Causale:</strong> Quota associativa UNASCA {anno} - {associate.codice}
          </p>
        </div>
      </div>

      {/* Upload distinta */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Distinta Bonifico (opzionale)
        </label>
        <div
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            file
              ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            onChange={handleFileChange}
            className="hidden"
          />
          {file ? (
            <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-400">
              <FileText className="w-5 h-5" />
              <span className="text-sm font-medium">{file.name}</span>
            </div>
          ) : (
            <div className="text-gray-500 dark:text-gray-400">
              <Upload className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">Clicca per caricare la distinta del bonifico</p>
              <p className="text-xs mt-1">PDF, JPG o PNG, max 10MB</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottoni */}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={processing}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Invio in corso...
            </>
          ) : (
            <>
              <Landmark className="w-5 h-5" />
              Conferma Bonifico
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Indietro
        </button>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 text-center flex items-center justify-center gap-1">
        <Info className="w-3 h-3" />
        La ricevuta verra emessa dopo la conferma della ricezione del bonifico.
      </p>
    </form>
  );
};

// ========================================
// RID Form
// ========================================
const RidForm: React.FC<{
  vociQuota: VoceQuota[];
  totale: number;
  rateRID: RataRID[];
  associate: AssociateData;
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ vociQuota, totale, rateRID, associate, onSuccess, onCancel }) => {
  const toast = useToast();
  const [iban, setIban] = useState('');
  const [processing, setProcessing] = useState(false);
  const [downloadingMandato, setDownloadingMandato] = useState(false);
  const [mandatoFile, setMandatoFile] = useState<File | null>(null);
  const [uploadingMandato, setUploadingMandato] = useState(false);
  const mandatoInputRef = useRef<HTMLInputElement>(null);

  const formatIban = (value: string) => {
    // Rimuovi tutto tranne alfanumerici e formatta a gruppi di 4
    const clean = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    return clean.match(/.{1,4}/g)?.join(' ') || clean;
  };

  const handleDownloadMandato = async () => {
    try {
      setDownloadingMandato(true);
      const blob = await downloadMandatoSepa();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mandato-sepa-${associate.codice || 'unasca'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Mandato SEPA scaricato');
    } catch (error) {
      console.error('Errore download mandato SEPA:', error);
      toast.error('Errore durante il download del mandato SEPA');
    } finally {
      setDownloadingMandato(false);
    }
  };

  const handleMandatoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo file PDF, JPG o PNG sono accettati');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Il file non puo superare 10MB');
      return;
    }
    setMandatoFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const cleanIban = iban.replace(/\s/g, '');
    if (cleanIban.length < 15) {
      toast.error('IBAN non valido');
      return;
    }

    setProcessing(true);
    try {
      // 1. Richiedi RID
      await requestRid(cleanIban, vociQuota);

      // 2. Upload mandato se presente. La richiesta RID è già registrata: un errore
      //    qui è solo un avviso (il mandato si ricarica dal riquadro del pagamento).
      let mandatoNonCaricato = false;
      if (mandatoFile) {
        setUploadingMandato(true);
        try {
          const formData = new FormData();
          formData.append('mandato', mandatoFile);
          await uploadMandatoSepa(formData);
        } catch (uploadError) {
          console.error('RID registrato ma upload del mandato fallito:', uploadError);
          mandatoNonCaricato = true;
        }
      }

      if (mandatoNonCaricato) {
        toast.warning(
          'Richiesta RID registrata; il mandato firmato non è stato caricato: riprova dal riquadro del pagamento',
          { duration: 10000 }
        );
      } else {
        toast.success('RID attivato. Le rate verranno addebitate alle scadenze indicate.', { duration: 5000 });
      }
      onSuccess();
    } catch (error: any) {
      console.error('Errore richiesta RID:', error);
      toast.error(error.response?.data?.message || 'Errore durante la richiesta RID');
    } finally {
      setProcessing(false);
      setUploadingMandato(false);
    }
  };

  // Calcola rate da visualizzare
  const rateDisplay = rateRID.length > 0
    ? rateRID
    : Array.from({ length: 3 }, (_, i) => {
        const importoRata = Math.round((totale / 3) * 100) / 100;
        const scadenza = new Date();
        scadenza.setMonth(scadenza.getMonth() + i + 1);
        return {
          numero: i + 1,
          importo: i === 2 ? totale - (importoRata * 2) : importoRata,
          scadenza: scadenza.toISOString()
        };
      });

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* IBAN */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          IBAN per addebito diretto
        </label>
        <input
          type="text"
          value={iban}
          onChange={(e) => setIban(formatIban(e.target.value))}
          placeholder="IT60 X054 2811 1010 0000 0123 456"
          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          maxLength={35}
        />
      </div>

      {/* Piano rate */}
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Piano di rateizzazione
        </h4>
        <div className="space-y-2">
          {rateDisplay.map((rata) => (
            <div key={rata.numero} className="flex items-center justify-between p-2.5 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-purple-500" />
                <span className="text-sm text-gray-900 dark:text-white">Rata {rata.numero}</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {rata.importo.toFixed(2)} EUR
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  entro {new Date(rata.scadenza).toLocaleDateString('it-IT')}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-between">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Totale</span>
          <span className="text-sm font-bold text-gray-900 dark:text-white">{totale.toFixed(2)} EUR</span>
        </div>
      </div>

      {/* Mandato SEPA */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleDownloadMandato}
            disabled={downloadingMandato}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg transition-colors"
          >
            {downloadingMandato ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Scarica mandato SEPA
          </button>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Carica mandato firmato (opzionale)
          </label>
          <div
            onClick={() => mandatoInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
              mandatoFile
                ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
          >
            <input
              ref={mandatoInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={handleMandatoUpload}
              className="hidden"
            />
            {mandatoFile ? (
              <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-400">
                <FileText className="w-4 h-4" />
                <span className="text-sm font-medium">{mandatoFile.name}</span>
              </div>
            ) : (
              <div className="text-gray-500 dark:text-gray-400">
                <Upload className="w-6 h-6 mx-auto mb-1" />
                <p className="text-xs">Clicca per caricare il mandato firmato</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottoni */}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={processing || iban.replace(/\s/g, '').length < 15}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {uploadingMandato ? 'Upload mandato...' : 'Elaborazione...'}
            </>
          ) : (
            <>
              <Banknote className="w-5 h-5" />
              Attiva RID
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Indietro
        </button>
      </div>
    </form>
  );
};

// ========================================
// Main Component
// ========================================
const AssociateFeePayment: React.FC<AssociateFeePaymentProps> = ({
  associate,
  onPaymentComplete,
  onClose,
  disableRid = false,
  overrideVociQuota,
  overrideTotale,
  paymentId
}) => {
  const [step, setStep] = useState<Step>('summary');
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [loading, setLoading] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [cardProcessing, setCardProcessing] = useState(false);
  const [cardSuccessInfo, setCardSuccessInfo] = useState<CardSuccessInfo | null>(null);

  // Settings data
  const [anno, setAnno] = useState<number>(new Date().getFullYear());
  const [quotaNazionale, setQuotaNazionale] = useState(0);
  const [quoteIntegrative, setQuoteIntegrative] = useState<VoceQuota[]>([]);
  const [quotaProvinciale, setQuotaProvinciale] = useState<VoceQuota | null>(null);
  const [totale, setTotale] = useState(0);
  const [rateRID, setRateRID] = useState<RataRID[]>([]);
  const [dettagliBancari, setDettagliBancari] = useState<DettagliBancari>({
    iban: '', banca: '', intestatario: '', bic: ''
  });
  const [metodiPagamento, setMetodiPagamento] = useState({ carta: true, bonifico: true, rid: true });
  const [categoria, setCategoria] = useState('');
  // 'rinnovo' quando l'associato in regola paga la quota durante la campagna rinnovi
  const [tipoQuota, setTipoQuota] = useState<'iscrizione' | 'rinnovo' | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Se abbiamo voci override (pagamento integrativo), usiamo quelle
        if (overrideVociQuota && overrideTotale) {
          setAnno(new Date().getFullYear());
          setQuotaNazionale(0);
          setQuoteIntegrative(overrideVociQuota);
          setQuotaProvinciale(null);
          setTotale(overrideTotale);
          // Carica comunque i dettagli bancari per il bonifico
          try {
            const res = await getPaymentSettings();
            setDettagliBancari(res.data.dettagliBancari || { iban: '', banca: '', intestatario: '', bic: '' });
            setMetodiPagamento(res.data.metodiPagamento || { carta: true, bonifico: true, rid: true });
          } catch {
            // Non bloccante
          }
          setLoading(false);
          return;
        }

        const res = await getPaymentSettings();
        const d = res.data;
        // Il gate lo decide il server: se dichiara la quota non pagabile non si procede
        // (campo assente con backend precedente → nessun blocco aggiuntivo)
        if (d.puoPagareQuota === false) {
          setSettingsError(d.motivoNonPagabile || 'Al momento non è possibile pagare la quota associativa.');
          return;
        }
        setTipoQuota(d.tipoPagamentoQuota ?? null);
        // Anno della quota: per un rinnovo anticipato è l'anno della campagna (corrente + 1)
        setAnno(annoDellaQuota(d));
        setCategoria(d.categoria);
        setQuotaNazionale(d.quotaNazionale);
        setQuoteIntegrative(d.quoteIntegrative || []);
        setQuotaProvinciale(d.quotaProvinciale || null);
        setTotale(d.totale);
        setRateRID(d.rateRID || []);
        setDettagliBancari(d.dettagliBancari || { iban: '', banca: '', intestatario: '', bic: '' });
        setMetodiPagamento(d.metodiPagamento || { carta: true, bonifico: true, rid: true });
      } catch (error: any) {
        console.error('Errore caricamento impostazioni:', error);
        // Senza importi dal server non si deve poter procedere al pagamento
        setSettingsError(error.response?.data?.message || 'Errore caricamento impostazioni pagamento');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, []);

  // Costruisci vociQuota per il backend
  const buildVociQuota = (): VoceQuota[] => {
    // Se pagamento integrativo, usa direttamente le voci override
    if (overrideVociQuota) {
      return overrideVociQuota;
    }

    const voci: VoceQuota[] = [
      { tipo: 'nazionale', descrizione: `Quota nazionale ${categoria}`, importo: quotaNazionale }
    ];
    for (const qi of quoteIntegrative) {
      voci.push(qi);
    }
    if (quotaProvinciale) {
      voci.push(quotaProvinciale);
    }
    return voci;
  };

  const handlePaymentSuccess = (info?: CardSuccessInfo) => {
    setCardSuccessInfo(info || null);
    setStep('success');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Caricamento impostazioni...</span>
      </div>
    );
  }

  // === ERRORE CARICAMENTO IMPOSTAZIONI: nessuna azione di pagamento ===
  if (settingsError) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>
        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">
          Pagamento non disponibile
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {settingsError}
        </p>
        <button
          onClick={onClose}
          className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm"
        >
          Chiudi
        </button>
      </div>
    );
  }

  // === CARTA ADDEBITATA, REGISTRAZIONE IN CORSO (stato bloccante) ===
  if (step === 'charged_pending') {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
        </div>
        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">
          Pagamento addebitato
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Pagamento addebitato: la registrazione è in corso, non ripetere il pagamento. Riceverai la conferma via email; in caso di dubbi contatta la segreteria.
        </p>
        <button
          onClick={onPaymentComplete}
          className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
        >
          Chiudi
        </button>
      </div>
    );
  }

  // === SUCCESS STEP ===
  if (step === 'success' && selectedMethod === 'carta' && cardSuccessInfo?.duplicate) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
        </div>
        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">
          Pagamento in verifica
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {cardSuccessInfo.message}
        </p>
        <button
          onClick={onPaymentComplete}
          className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
        >
          Chiudi
        </button>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">
          Operazione completata
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {selectedMethod === 'carta' && 'Il pagamento e stato confermato. La ricevuta e disponibile per il download.'}
          {selectedMethod === 'bonifico' && 'Il bonifico e stato registrato. Riceverai conferma dalla segreteria.'}
          {selectedMethod === 'rid' && 'Il RID e stato attivato. Le rate verranno addebitate alle scadenze indicate.'}
        </p>
        <button
          onClick={onPaymentComplete}
          className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
        >
          Chiudi
        </button>
      </div>
    );
  }

  // === PAYMENT STEP (method-specific) ===
  if (step === 'payment' && selectedMethod) {
    const vociQuota = buildVociQuota();

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => setStep('method')}
            disabled={cardProcessing}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h3 className="text-base font-bold text-gray-900 dark:text-white">
            {selectedMethod === 'carta' && 'Pagamento con Carta'}
            {selectedMethod === 'bonifico' && 'Pagamento con Bonifico'}
            {selectedMethod === 'rid' && 'Pagamento con RID'}
          </h3>
        </div>

        {/* Riepilogo importo */}
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
          <p className="text-sm text-purple-900 dark:text-purple-100">
            <strong>Importo:</strong> {totale.toFixed(2)} EUR - Quota associativa {anno}
          </p>
        </div>

        {selectedMethod === 'carta' && (
          <Elements stripe={stripePromise}>
            <CardPaymentForm
              vociQuota={vociQuota}
              totale={totale}
              paymentId={paymentId}
              associateId={associate.id}
              anno={anno}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setStep('method')}
              onAmountChanged={(amount) => setTotale(amount)}
              onStale={onPaymentComplete}
              onChargedPending={() => { setCardProcessing(false); setStep('charged_pending'); }}
              onProcessingChange={setCardProcessing}
            />
          </Elements>
        )}

        {selectedMethod === 'bonifico' && (
          <BankTransferForm
            vociQuota={vociQuota}
            totale={totale}
            dettagliBancari={dettagliBancari}
            associate={associate}
            anno={anno}
            paymentId={paymentId}
            onSuccess={handlePaymentSuccess}
            onCancel={() => setStep('method')}
          />
        )}

        {selectedMethod === 'rid' && (
          <RidForm
            vociQuota={vociQuota}
            totale={totale}
            rateRID={rateRID}
            associate={associate}
            onSuccess={handlePaymentSuccess}
            onCancel={() => setStep('method')}
          />
        )}
      </div>
    );
  }

  // === METHOD SELECTION STEP ===
  if (step === 'method') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => setStep('summary')}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h3 className="text-base font-bold text-gray-900 dark:text-white">
            Metodo di Pagamento
          </h3>
        </div>

        <div className="space-y-3">
          {/* Carta di credito */}
          {metodiPagamento.carta && (
            <button
              onClick={() => { setSelectedMethod('carta'); setStep('payment'); }}
              className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-600 rounded-lg text-left transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors">
                  <CreditCard className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Carta di credito</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Pagamento immediato e sicuro via Stripe</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
              </div>
            </button>
          )}

          {/* Bonifico bancario */}
          {metodiPagamento.bonifico && (
            <button
              onClick={() => { setSelectedMethod('bonifico'); setStep('payment'); }}
              className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-600 rounded-lg text-left transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                  <Landmark className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Bonifico bancario</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Verifica entro 1-2 giorni lavorativi</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
              </div>
            </button>
          )}

          {/* RID */}
          {metodiPagamento.rid && !disableRid && (
            <button
              onClick={() => { setSelectedMethod('rid'); setStep('payment'); }}
              className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-600 rounded-lg text-left transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
                  <Banknote className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">RID bancario</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Addebito diretto SEPA in {rateRID.length || 3} rate</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
              </div>
            </button>
          )}

          {!metodiPagamento.carta && !metodiPagamento.bonifico && !metodiPagamento.rid && (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              Nessun metodo di pagamento disponibile al momento. Contatta la segreteria.
            </p>
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          Annulla
        </button>
      </div>
    );
  }

  // === SUMMARY STEP (default) ===
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-bold text-gray-900 dark:text-white">
          {tipoQuota === 'rinnovo' && !overrideVociQuota ? `Rinnova la quota ${anno}` : `Riepilogo Quota Associativa ${anno}`}
        </h3>
        <button
          onClick={onClose}
          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          Chiudi
        </button>
      </div>

      {/* Tabella quote */}
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">Quota nazionale ({categoria})</td>
              <td className="px-4 py-2.5 text-right font-semibold text-gray-900 dark:text-white">{quotaNazionale.toFixed(2)} EUR</td>
            </tr>
            {quoteIntegrative.map((qi, i) => (
              <tr key={i} className="border-b border-gray-200 dark:border-gray-700">
                <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{qi.descrizione}</td>
                <td className="px-4 py-2.5 text-right font-semibold text-gray-900 dark:text-white">{qi.importo.toFixed(2)} EUR</td>
              </tr>
            ))}
            {quotaProvinciale && (
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{quotaProvinciale.descrizione}</td>
                <td className="px-4 py-2.5 text-right font-semibold text-gray-900 dark:text-white">{quotaProvinciale.importo.toFixed(2)} EUR</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-purple-50 dark:bg-purple-900/20">
              <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">Totale</td>
              <td className="px-4 py-3 text-right font-bold text-purple-700 dark:text-purple-300 text-base">{totale.toFixed(2)} EUR</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Bottone procedi */}
      <button
        onClick={() => setStep('method')}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-lg shadow-md transition-all"
      >
        Procedi al pagamento
        <ArrowRight className="w-4 h-4" />
      </button>

      <button
        onClick={onClose}
        className="w-full px-4 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
      >
        Annulla
      </button>
    </div>
  );
};

export default AssociateFeePayment;
